/**
 * Recipe list query helpers.
 *
 * Collection selection is owned by route/screen state; core-ui only derives the virtualized query
 * inputs for a specific workspace collection.
 */
import { useMemo } from "react";
import type { Zero } from "@rocicorp/zero";
import { Recipe } from "@corpuscomplete/core/nutrition/recipe";
import { queries } from "@corpuscomplete/core/zero";
import { FoodCollectionUI } from "../../food-collection";
import { ZeroVirtualCoreArgs } from "../../../zero";

export namespace RecipeList {
  export function useVirtualArgs(
    collectionId: string,
  ): ZeroVirtualCoreArgs<{ collectionId: string }, Recipe.ListItem, Recipe.ListCursor> {
    return useMemo(
      () => ({
        listContextParams: { collectionId },
        getRowKey: (item) => item.id,
        toStartRow: (item) => ({ id: item.id }),
        getPageQuery: ({ limit, start, dir }) => ({
          query: queries.recipe.listItems({
            collectionId,
            limit,
            cursor: start,
            dir,
          }),
        }),
        getSingleQuery: ({ id }) => ({
          query: queries.recipe.listItemById({
            collectionId,
            id,
          }),
        }),
      }),
      [collectionId],
    );
  }

  export async function preload(
    zero: Zero,
    {
      requestedCollectionId,
      scopeId,
    }: {
      requestedCollectionId?: string;
      scopeId: string;
    },
  ): Promise<void> {
    const manageableCollections = await zero.run(queries.foodCollection.forContentManagement());
    const collectionId = FoodCollectionUI.resolveManagedCollectionId({
      requestedCollectionId,
      collections: manageableCollections.filter((collection) => collection.scopeId === scopeId),
      foodType: "recipe",
    });
    if (!collectionId) {
      return;
    }

    void zero.run(
      queries.recipe.listItems({
        collectionId,
        limit: 100,
        dir: "forward",
        cursor: null,
      }),
    );
  }
}
